// src/modules/units/tyres/views/retread-batch/retreadbatchform.js
//
// Orchestrator for the create / edit DRAFT form. Fetches the suppliers, validates
// (supplier is required), and submits through the batch API client — which emits
// RETREAD_BATCH_CHANGED on success, so the list behind refetches without this form
// telling it to. A thin fetch-and-swap shell; all markup is in the pure view.
//
// Writes are administrator-only on the backend, so a 403 is surfaced as a clear
// "administrator access needed" line rather than a generic failure.

import { createRetreadBatch, updateRetreadBatch } from '../retread-batch.api.js';
import { fetchSuppliers } from '../suppliers.api.js';
import { fetchTyreCatalogue } from '../tyre-catalogue.api.js';
import { batchFormHTML, formStateHTML } from './retreadbatchform.view.js';

function el(html) {
  const t = document.createElement('template');
  t.innerHTML = html.trim();
  return t.content.firstElementChild;
}

// date-input value (YYYY-MM-DD) -> ISO at local noon, which never slips to the
// previous day under UTC conversion. Matches the tyre action form's convention.
function isoFromDateInput(value) {
  if (!value) return undefined;
  const [y, m, d] = String(value).split('-').map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d, 12, 0, 0).toISOString();
}

/**
 * @param {HTMLElement} viewEl controller view slot (emptied first)
 * @param {object} ctx
 * @param {'create'|'edit'} ctx.mode
 * @param {object} [ctx.batch] batch being edited (edit mode)
 * @param {() => void} [ctx.onClose] cancel, or a successful edit
 * @param {(batch:object)=>void} [ctx.onSaved] a successful create/edit (gets the saved batch)
 * @returns {() => void} teardown
 */
export function renderRetreadBatchForm(viewEl, ctx = {}) {
  let disposed = false;
  const mode = ctx.mode === 'edit' ? 'edit' : 'create';
  let suppliers = [];
  let catalogue = [];

  const root = el(`<div class="tm-rb-form-root">${formStateHTML('loading', 'Loading…')}</div>`);
  viewEl.replaceChildren(root);
  const setContent = (html) => { root.innerHTML = html; };

  // ---- rendered-form wiring (re-bound each time the form is (re)drawn) ----
  function bindForm() {
    const msgEl = root.querySelector('[data-rb-msg]');
    const saveBtn = root.querySelector('[data-rb-save]');
    const cancelBtn = root.querySelector('[data-rb-cancel]');
    const fields = Object.fromEntries(
      Array.from(root.querySelectorAll('[data-rb-field]')).map((n) => [n.dataset.rbField, n])
    );

    function setMsg(text, kind) {
      if (!msgEl) return;
      msgEl.textContent = text || '';
      msgEl.hidden = !text;
      msgEl.className = `tm-form-msg${kind ? ` tm-form-msg-${kind}` : ''}`;
    }
    function setBusy(on) {
      if (saveBtn) saveBtn.disabled = on;
      if (cancelBtn) cancelBtn.disabled = on;
      Object.values(fields).forEach((n) => { n.disabled = on; });
    }

    async function save() {
      const catalogueId = fields.tyreCatalogueEntryId?.value || '';   // create mode only
      const supplierId = fields.supplierId?.value || '';

      if (mode === 'create' && !catalogueId) {
        setMsg('Choose the retread product for this batch.', 'error');
        fields.tyreCatalogueEntryId?.focus();
        return;
      }
      if (!supplierId) {
        setMsg('Choose a supplier for this batch.', 'error');
        fields.supplierId?.focus();
        return;
      }

      const payload = { supplierId };
      if (mode === 'create') payload.tyreCatalogueEntryId = catalogueId;
      const iso = isoFromDateInput(fields.expectedReturnDate?.value);
      if (iso) payload.expectedReturnDate = iso;
      const notes = (fields.notes?.value || '').trim();
      if (notes) payload.notes = notes;

      setBusy(true);
      setMsg(mode === 'edit' ? 'Saving changes…' : 'Creating batch…');

      const res = mode === 'edit'
        ? await updateRetreadBatch(ctx.batch?.retreadBatchId, payload, { reason: 'edit' })
        : await createRetreadBatch(payload, { reason: 'create' });

      if (disposed) return;

      if (!res.success) {
        setBusy(false);
        if (res.status === 403) {
          setMsg('You need administrator access to save a retread batch.', 'error');
        } else if (res.code === 'TYRE_CATALOGUE_ENTRY_NOT_FOUND') {
          // the chosen product was deactivated or removed since the form loaded
          setMsg('That retread product isn’t available anymore — pick another.', 'error');
          fields.tyreCatalogueEntryId?.focus();
        } else if (res.locked) {
          setMsg('This batch is busy with another operation. Try again in a moment.', 'error');
        } else {
          setMsg(res.message || 'Could not save the batch.', 'error');
        }
        return;
      }

      // Success. The API client has already emitted RETREAD_BATCH_CHANGED.
      if (mode === 'edit') {
        if (typeof ctx.onClose === 'function') ctx.onClose();       // detail behind refetches on the event
      } else if (typeof ctx.onSaved === 'function') {
        ctx.onSaved(res.batch || {});                                // land on the new batch's detail
      }
    }

    saveBtn?.addEventListener('click', save);
    cancelBtn?.addEventListener('click', () => { if (typeof ctx.onClose === 'function') ctx.onClose(); });
    Object.values(fields).forEach((n) => {
      if (n.tagName === 'TEXTAREA') return;
      n.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); save(); } });
    });

    (fields.tyreCatalogueEntryId || fields.supplierId)?.focus();
  }

  function showForm() {
    setContent(batchFormHTML({ mode, batch: ctx.batch || {}, suppliers, catalogue }));
    bindForm();
  }

  async function loadFormData() {
    setContent(formStateHTML('loading', 'Loading…'));
    // Suppliers always; the catalogue only when creating — the product is fixed
    // after creation, so the edit form doesn't offer it.
    const [supRes, catRes] = await Promise.all([
      fetchSuppliers({ sort: 'supplierName' }),
      mode === 'create' ? fetchTyreCatalogue({ sort: 'manufacturerName,modelName' }) : Promise.resolve(null)
    ]);
    if (disposed) return;

    if (!supRes.success) {
      setContent(formStateHTML('error', supRes.message || 'Could not load suppliers.', { retry: true }));
      return;
    }
    suppliers = Array.isArray(supRes.data?.results) ? supRes.data.results : [];
    if (!suppliers.length) {
      // A batch needs a supplier; point the user at where to add one rather than
      // showing a form they can't complete.
      setContent(formStateHTML('empty', 'No suppliers yet — add one before creating a retread batch.', { addSupplier: true }));
      return;
    }

    if (mode === 'create') {
      if (!catRes || !catRes.success) {
        setContent(formStateHTML('error', (catRes && catRes.message) || 'Could not load the tyre catalogue.', { retry: true }));
        return;
      }
      // /choices would be the active-only source, but it's unused elsewhere and its
      // shape isn't pinned; the full list has a proven shape, so filter active here.
      catalogue = (Array.isArray(catRes.data?.results) ? catRes.data.results : [])
        .filter((e) => e?.isActive !== false);
      if (!catalogue.length) {
        setContent(formStateHTML('empty', 'No active retread products in the catalogue — add one in the Tyre Catalogue first.'));
        return;
      }
    }
    showForm();
  }

  root.addEventListener('click', (e) => {
    if (e.target.closest('[data-rb-retry]')) { loadFormData(); return; }
    if (e.target.closest('[data-rb-add-supplier]') && typeof ctx.onAddSupplier === 'function') ctx.onAddSupplier();
  });

  loadFormData();

  return () => { disposed = true; };
}
